import { CancelToken } from 'axios';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getTrainingList, selectTrainingList, trainingListActions } from '../../store/entities/trainingList';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { Grid } from '../common/grid/Grid';
import { SectionHeader } from '../common/SectionHeader';
import { TrainingFilter } from './TrainingFilter';
import { getCompanyChecks, selectCompanyChecksCanCreateTraining, selectCompanyChecksState } from '../../store/entities/companyChecks';
import { getLangNameObj, joinStrings2 } from '../../utils/format';
import { TrainingDto } from '@api/models/training/TrainingDto';

export const Training: FC = () => {
  const { t, i18n } = useTranslation();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const trainingList = useAppSelector(selectTrainingList);
  const companyChecksCanCreateTraining = useAppSelector(selectCompanyChecksCanCreateTraining);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCompanyChecks());
  }, [identityCompanyId, dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getTrainingList(cancelToken));
  };

  const handleRenderData = (item: TrainingDto) => {
    return (
      <>
        <QcsTableCell>{getLangNameObj(i18n, item)}</QcsTableCell>
        <QcsTableCell>{joinStrings2(item.questionnaire?.map((x) => getLangNameObj(i18n, x)))}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{t('entityState.' + item.state)}</QcsTableCell>
      </>
    );
  };

  const handleRowClick = (item: TrainingDto) => {
    navigate(`/training/${item.id}`);
  };

  const handleAddClick = () => {
    navigate('/training/new');
  };

  return (
    <>
      <SectionHeader title="training.title" addText="training.add" addDisabled={!companyChecksCanCreateTraining} handleAddClick={handleAddClick} />
      <Grid<TrainingDto>
        fetchStateSelector={selectCompanyChecksState}
        headers={[
          { captionStr: 'training.name', orderName: 'name' },
          {
            captionStr: 'training.questionnaires',
            orderName: 'questionnaire',
          },
          {
            captionStr: 'training.state',
            orderName: 'state',
            hideOnMobile: true,
          },
        ]}
        data={trainingList}
        gridActions={trainingListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
        filter={<TrainingFilter query={trainingList.query} />}
      />
    </>
  );
};
